import { useAuthStore } from "@shared/src/stores/useAuthStore";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@shared/src/components/ui/input-otp";
import { Button } from "@shared/src/components/ui/button";
import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@shared/src/components/ui/form";
import { Spinner } from "@shared/src/components/common/misc/Spinner";
import { useAuthenticate } from "@account-kit/react";
import useAuth from "@shared/src/hooks/auth/useAuth";
import { ixToast } from "@shared/src/utils/ixToast";

const schema = z.object({
  code: z.string().length(6, "Please enter all digits").regex(/^\d+$/, "Please enter only numbers"),
});

type FormValues = z.infer<typeof schema>;

const OTP_LENGTH = 6;

export function EmailVerification() {
  const { email, setIsVerifying, resetEmail } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const { authenticate } = useAuth();
  const [otpError, setOtpError] = useState<string | null>(null);
  const [isOtpSubmitted, setIsOtpSubmitted] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      code: "",
    },
  });

  const handleVerify = useCallback(
    async (data: FormValues) => {
      setIsLoading(true);
      setOtpError(null); // Reset error state before new attempt
      try {
        await authenticate({
          type: "otp",
          otpCode: data.code,
          connectorIcon: "/images/email.svg",
          connectorId: "alchemy",
        });
        // Don't close the dialog here - let use-auth.ts handle it based on account existence
        setIsVerifying(false);
      } catch (error: any) {
        setOtpError(error.message || "Invalid verification code");
        ixToast.error(error.message || "Failed to verify code");
        form.reset(); // Reset form on error
      } finally {
        setIsLoading(false);
      }
    },
    [form, setIsVerifying, authenticate],
  );

  const handleResend = async () => {
    setIsResending(true);
    setOtpError(null); // Reset error state before resend
    try {
      await authenticate({
        type: "email",
        email: email,
        emailMode: "otp",
        connectorId: "alchemy",
      });
      form.reset(); // Reset form after resend
      ixToast.success("Verification code resent");
    } catch (error: any) {
      ixToast.error(error.message || "Failed to resend code");
    } finally {
      setIsResending(false);
    }
  };

  // Reset error state when component unmounts
  useEffect(() => {
    return () => {
      setOtpError(null);
    };
  }, []);

  const otp = form.watch("code");
  // Watch for code changes and auto-submit when complete
  useEffect(() => {
    console.log("initialSignInToGetSigner, subscription", otp);
    if (isOtpSubmitted) return;
    if (otp?.length === OTP_LENGTH) {
      setIsOtpSubmitted(true);
      form.handleSubmit(handleVerify)();
    }
    return () => {};
  }, [form, handleVerify, isOtpSubmitted, otp]);

  return (
    <div className="flex flex-col items-center gap-12 w-full max-w-md mx-auto">
      <div className="space-y-2 text-center">
        <p className="text-text-tertiary">Enter the verification code sent to</p>
        <p className="text-text-primary font-extrabold">{email}</p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleVerify)} className="w-full space-y-8">
          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <InputOTP
                    maxLength={OTP_LENGTH}
                    value={field.value}
                    onChange={field.onChange}
                    className={`w-full ${otpError ? "animate-shake" : ""}`}
                    pattern="\d*"
                    inputMode="numeric"
                  >
                    <InputOTPGroup
                      className="w-full grid gap-2"
                      style={{
                        gridTemplateColumns: `repeat(${OTP_LENGTH}, 1fr)`,
                      }}
                    >
                      {Array.from({ length: OTP_LENGTH }).map((_, i) => (
                        <div key={i} className="aspect-square w-full">
                          <InputOTPSlot
                            index={i}
                            className={`!w-full !h-full text-2xl !rounded-2xl ${
                              otpError ? "border-error-500" : "border-border-tertiary"
                            } bg-transparent text-text-primary`}
                          />
                        </div>
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Submit button - commented out for now as we're using auto-submit
          <div className="flex justify-center">
            <Button
              type="submit"
              disabled={isLoading}
              className="py-7 px-16 bg-button-tertiary-bg text-text-primaryinverse rounded-full"
            >
              {isLoading ? <Spinner className="text-text-primaryinverse" /> : "Verify"}
            </Button>
          </div>
          */}

          {/* Loading indicator shown when auto-submitting */}
          {isLoading && (
            <div className="flex justify-center">
              <Spinner />
            </div>
          )}
        </form>
      </Form>

      <button
        onClick={handleResend}
        disabled={isResending}
        className="text-text-brand hover:opacity-75 text-small disabled:opacity-50"
      >
        {isResending ? (
          <span className="flex items-center gap-2">
            <Spinner size={16} className="text-text-brand" />
            Resending...
          </span>
        ) : (
          "Resend Verification code"
        )}
      </button>
    </div>
  );
}
